import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { motion, AnimatePresence } from 'framer-motion';
import { alpha } from '@mui/material/styles';

// Order matches the actual page layout in App.js
const navItems = [
  { label: 'Home',           href: '#home',           id: 'home'           },
  { label: 'Experience',     href: '#experience',     id: 'experience'     },
  { label: 'Projects',       href: '#projects',       id: 'projects'       },
  { label: 'Skills',         href: '#skills',         id: 'skills'         },
  { label: 'Certifications', href: '#certifications', id: 'certifications' },
];

const RESUME_LINK = '/Resume/Resume - Syed Saad Waqar.pdf';

function NavBar() {
  const [scrolled, setScrolled]     = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeId, setActiveId]     = useState('home');

  // Navbar background on scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Active section: pick the last section whose top has crossed 35% down the viewport
  useEffect(() => {
    const onScroll = () => {
      const threshold = window.innerHeight * 0.35;
      let current = navItems[0].id;
      for (const { id } of navItems) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= threshold) {
          current = id;
        }
      }
      setActiveId(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // set correct state on mount
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (id) => {
    setActiveId(id);
    setMobileOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: scrolled
            ? (theme) => alpha(theme.palette.background.default, 0.9)
            : 'transparent',
          backdropFilter: scrolled ? 'blur(24px) saturate(200%)' : 'none',
          borderBottom: scrolled
            ? (theme) => `1px solid ${alpha(theme.palette.divider, 0.12)}`
            : '1px solid transparent',
          transition: 'background 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease',
          zIndex: 1300,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            px: { xs: 2.5, sm: 3, md: 5 },
            minHeight: { xs: '62px', md: '70px' },
          }}
        >
          {/* ── Logo ── */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <Typography
              component="a"
              href="#home"
              onClick={() => handleNavClick('home')}
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.2rem', md: '1.35rem' },
                background: 'linear-gradient(135deg, #f8fafc 0%, #818cf8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textDecoration: 'none',
                letterSpacing: '-0.03em',
                cursor: 'pointer',
                display: 'block',
                userSelect: 'none',
              }}
            >
              SSW
            </Typography>
          </motion.div>

          {/* ── Desktop Nav ── */}
          <Box
            component="nav"
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 0.5,
              background: scrolled
                ? (theme) => alpha(theme.palette.background.paper, 0.5)
                : 'transparent',
              border: scrolled
                ? (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`
                : '1px solid transparent',
              borderRadius: '14px',
              px: scrolled ? 1 : 0,
              py: scrolled ? 0.5 : 0,
              transition: 'all 0.3s ease',
            }}
          >
            {navItems.map((item, i) => {
              const isActive = activeId === item.id;
              return (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 + 0.15, ease: 'easeOut' }}
                >
                  <Button
                    component="a"
                    href={item.href}
                    data-cursor="hover"
                    onClick={() => handleNavClick(item.id)}
                    sx={{
                      position: 'relative',
                      color: isActive ? 'primary.main' : 'text.secondary',
                      fontWeight: isActive ? 600 : 500,
                      fontSize: '0.875rem',
                      px: 1.5,
                      py: 0.75,
                      borderRadius: '10px',
                      letterSpacing: 0,
                      textTransform: 'none',
                      minWidth: 0,
                      background: isActive
                        ? (theme) => alpha(theme.palette.primary.main, 0.1)
                        : 'transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: 'text.primary',
                        background: (theme) => alpha(theme.palette.primary.main, 0.08),
                      },
                      // Active underline dot
                      '&::after': isActive ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 4,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        background: 'primary.main',
                        backgroundColor: '#6366f1',
                      } : {},
                    }}
                  >
                    {item.label}
                  </Button>
                </motion.div>
              );
            })}
          </Box>

          {/* ── Desktop CTA Buttons ── */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.45, ease: 'easeOut' }}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {/* Resume Download — recruiters love this upfront */}
            <Button
              component="a"
              href={RESUME_LINK}
              download
              data-cursor="hover"
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
              size="small"
              sx={{
                display: { xs: 'none', md: 'flex' },
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '0.8375rem',
                textTransform: 'none',
                px: 1.75,
                py: 0.75,
                borderRadius: '10px',
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.25)}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: 'text.primary',
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
                  background: (theme) => alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              Resume
            </Button>

            {/* Hire Me */}
            <Button
              component="a"
              href="mailto:waqarsaad76@gmail.com"
              variant="contained"
              data-cursor="hover"
              size="small"
              sx={{
                display: { xs: 'none', md: 'flex' },
                px: 2.5,
                py: 0.875,
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                boxShadow: '0 4px 14px rgba(99,102,241,0.3)',
                transition: 'all 0.25s ease',
                whiteSpace: 'nowrap',
                '&:hover': {
                  background: 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)',
                  boxShadow: '0 6px 20px rgba(99,102,241,0.45)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Hire Me
            </Button>

            {/* Mobile Hamburger */}
            <IconButton
              onClick={() => setMobileOpen(true)}
              data-cursor="hover"
              aria-label="Open navigation menu"
              sx={{
                display: { md: 'none' },
                color: 'text.secondary',
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: '10px',
                p: 0.75,
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: 'text.primary',
                  background: (theme) => alpha(theme.palette.primary.main, 0.08),
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
                },
              }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </motion.div>
        </Toolbar>
      </AppBar>

      {/* ── Mobile Drawer ── */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            background: (theme) => alpha(theme.palette.background.default, 0.97),
            backdropFilter: 'blur(28px)',
            borderLeft: (theme) => `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          },
        }}
      >
        {/* Drawer Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary', lineHeight: 1.2 }}>
              Syed Saad Waqar
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', fontWeight: 500 }}>
              Full-Stack &amp; AI Developer
            </Typography>
          </Box>
          <IconButton
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation menu"
            size="small"
            sx={{
              color: 'text.secondary',
              border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: '8px',
              p: 0.625,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: (theme) => alpha(theme.palette.divider, 0.1) }} />

        {/* Nav Links */}
        <List sx={{ pt: 1.5, px: 1.5 }}>
          <AnimatePresence>
            {navItems.map((item, i) => {
              const isActive = activeId === item.id;
              return (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      component="a"
                      href={item.href}
                      onClick={() => handleNavClick(item.id)}
                      sx={{
                        px: 2,
                        py: 1.25,
                        borderRadius: '10px',
                        background: isActive
                          ? (theme) => alpha(theme.palette.primary.main, 0.1)
                          : 'transparent',
                        border: isActive
                          ? (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          : '1px solid transparent',
                        '&:hover': {
                          background: (theme) => alpha(theme.palette.primary.main, 0.07),
                        },
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontWeight: isActive ? 600 : 500,
                          fontSize: '0.9375rem',
                          color: isActive ? 'primary.main' : 'text.primary',
                        }}
                      />
                      {isActive && (
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </List>

        <Divider sx={{ mx: 2, borderColor: (theme) => alpha(theme.palette.divider, 0.1) }} />

        {/* Mobile CTAs */}
        <Box sx={{ px: 2.5, pt: 2.5, pb: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Button
            component="a"
            href={RESUME_LINK}
            download
            variant="outlined"
            fullWidth
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{
              py: 1.25,
              fontWeight: 600,
              fontSize: '0.9rem',
              textTransform: 'none',
              borderRadius: '10px',
              borderColor: (theme) => alpha(theme.palette.divider, 0.3),
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                background: (theme) => alpha(theme.palette.primary.main, 0.06),
              },
            }}
          >
            Download Resume
          </Button>
          <Button
            component="a"
            href="mailto:waqarsaad76@gmail.com"
            variant="contained"
            fullWidth
            sx={{
              py: 1.25,
              fontWeight: 600,
              fontSize: '0.9375rem',
              textTransform: 'none',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)',
                boxShadow: '0 6px 18px rgba(99,102,241,0.45)',
              },
            }}
          >
            Hire Me
          </Button>
        </Box>
      </Drawer>
    </>
  );
}

export default NavBar;
